import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/app/AppShell';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { InputField } from '../../components/ui/InputField';
import { useToast } from '../../components/ui/Toast';
import { api, ApiError } from '../../lib/api';
import type { Me, Organization, OrganizationMember } from '../../lib/types';
import { OrganizationGroupsSection } from './OrganizationGroupsSection';
import { OrganizationRolesModal } from './OrganizationRolesModal';

const navItems = [
  { label: 'Карта', to: '/app' },
  { label: 'Реквизиты', to: '/app/organization' },
  { label: 'Участники', to: '/app/organization' },
  { label: 'Роли и группы', to: '/app/organization' },
  { label: 'Журнал действий', to: '/app/organization/audit-log' },
];

const MEMBER_LIMIT = 10;

const STATUS_LABEL: Record<OrganizationMember['status'], string> = {
  invited: 'Приглашён',
  active: 'Активен',
  blocked: 'Заблокирован',
  left: 'Покинул',
};

function memberName(m: OrganizationMember): string {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return full || m.email || '—';
}

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

export function OrganizationSettingsPage() {
  const { showToast } = useToast();
  const [me, setMe] = useState<Me | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingOrg, setEditingOrg] = useState(false);
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [inn, setInn] = useState('');
  const [kpp, setKpp] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgSaveError, setOrgSaveError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [rolesModalOpen, setRolesModalOpen] = useState(false);

  const applyOrg = (o: Organization) => {
    setOrg(o);
    setName(o.name);
    setLegalName(o.legal_name ?? '');
    setInn(o.inn ?? '');
    setKpp(o.kpp ?? '');
  };

  const load = async () => {
    try {
      const [meResponse, orgResponse, membersResponse] = await Promise.all([
        api.getMe(),
        api.getOrganization(),
        api.listOrganizationMembers(),
      ]);
      setMe(meResponse);
      applyOrg(orgResponse);
      setMembers(membersResponse);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить организацию.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleStartEditOrg = () => {
    if (org) applyOrg(org);
    setOrgSaveError(null);
    setEditingOrg(true);
  };

  const handleCancelEditOrg = () => {
    if (org) applyOrg(org);
    setOrgSaveError(null);
    setEditingOrg(false);
  };

  const handleSaveOrg = async () => {
    setOrgSaveError(null);
    setSavingOrg(true);
    try {
      const updated = await api.updateOrganization({
        name,
        legal_name: legalName || undefined,
        inn: inn || undefined,
        kpp: kpp || undefined,
      });
      applyOrg(updated);
      setEditingOrg(false);
      showToast('success', 'Реквизиты сохранены');
    } catch (err) {
      setOrgSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить реквизиты.');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviting(true);
    try {
      const member = await api.inviteOrganizationMember(inviteEmail.trim());
      setMembers((prev) => [...prev, member]);
      setInviteEmail('');
      if (member.email_sent === false) {
        showToast('error', `Участник ${member.email} добавлен, но письмо-приглашение не отправилось. Сообщите ему вручную.`);
      } else if (member.email_sent === true) {
        showToast('success', `Приглашение отправлено на ${member.email}`);
      } else {
        showToast('success', `${member.email} уже был в системе — добавлен в организацию сразу`);
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Не удалось пригласить участника.';
      setInviteError(message);
      showToast('error', message);
    } finally {
      setInviting(false);
    }
  };

  const handleToggleAdmin = async (member: OrganizationMember) => {
    try {
      const updated = await api.setOrganizationMemberAdmin(member.id, !member.is_admin);
      setMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Не удалось изменить права участника.');
    }
  };

  const handleRemove = async (member: OrganizationMember) => {
    try {
      await api.removeOrganizationMember(member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Не удалось удалить участника.');
    }
  };

  const isAdmin = org?.is_admin ?? false;
  const activeMembers = members.filter((m) => m.status !== 'left');

  return (
    <AppShell
      orgLabel={org?.name ?? '—'}
      navItems={navItems}
      backLabel="Вернуться на карту"
      backTo="/app"
      title="Организация"
      breadcrumb="Личный кабинет / Организация"
      userInitials={initialsOf(me)}
      userAvatarUrl={me?.avatar_url}
    >
      {loadError && <p className="mb-4 text-xs font-medium text-status-warning">{loadError}</p>}

      {isAdmin && (
        <div className="mx-auto mb-6 flex max-w-[1200px] gap-3">
          <button
            type="button"
            onClick={() => setRolesModalOpen(true)}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Роли участников по пространствам
          </button>
          <Link
            to="/app/organization/audit-log"
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Журнал действий
          </Link>
        </div>
      )}

      <div className="mx-auto grid max-w-[1200px] grid-cols-[1fr_420px] gap-6">
        <Card
          title="Реквизиты"
          action={
            isAdmin && !editingOrg ? (
              <button
                type="button"
                onClick={handleStartEditOrg}
                className="text-[11px] font-semibold text-action-primary hover:underline"
              >
                Изменить
              </button>
            ) : undefined
          }
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {editingOrg ? (
              <>
                <InputField label="Название" value={name} onChange={(e) => setName(e.target.value)} />
                <InputField label="Юридическое название" value={legalName} onChange={(e) => setLegalName(e.target.value)} />
                <InputField label="ИНН" value={inn} onChange={(e) => setInn(e.target.value)} />
                <InputField label="КПП" value={kpp} onChange={(e) => setKpp(e.target.value)} />
              </>
            ) : (
              <>
                <ReadOnlyField label="Название" value={org?.name} />
                <ReadOnlyField label="Юридическое название" value={org?.legal_name} />
                <ReadOnlyField label="ИНН" value={org?.inn} />
                <ReadOnlyField label="КПП" value={org?.kpp} />
              </>
            )}
          </div>

          {orgSaveError && <p className="mt-3 text-xs font-medium text-status-warning">{orgSaveError}</p>}

          {editingOrg && (
            <div className="mt-6 flex gap-3">
              <Button type="button" className="w-[180px]" onClick={handleSaveOrg} disabled={savingOrg}>
                {savingOrg ? 'Сохраняем…' : 'Сохранить'}
              </Button>
              <button
                type="button"
                onClick={handleCancelEditOrg}
                disabled={savingOrg}
                className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Отмена
              </button>
            </div>
          )}
        </Card>

        <Card title={`Участники (${activeMembers.length}/${MEMBER_LIMIT})`}>
          {isAdmin && (
            <form onSubmit={handleInvite} className="mb-5 flex items-end gap-2">
              <div className="flex-1">
                <InputField
                  label="Пригласить по почте"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="name@company.ru"
                  required
                />
              </div>
              <Button
                type="submit"
                className="h-[38px] shrink-0 px-4"
                disabled={inviting || activeMembers.length >= MEMBER_LIMIT}
              >
                {inviting ? '…' : 'Пригласить'}
              </Button>
            </form>
          )}
          {inviteError && <p className="mb-3 text-xs font-medium text-status-warning">{inviteError}</p>}

          <div className="flex flex-col divide-y divide-border-default">
            {activeMembers.length === 0 && (
              <p className="py-3 text-xs text-text-secondary">Нет участников.</p>
            )}
            {activeMembers.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-text-primary">{memberName(m)}</p>
                  <p className="mt-1 truncate text-[10px] text-text-secondary">
                    {m.email} · {STATUS_LABEL[m.status]}
                    {m.is_admin ? ' · Администратор' : ''}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 items-center gap-3">
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-action-primary"
                      onClick={() => handleToggleAdmin(m)}
                    >
                      {m.is_admin ? 'Снять права' : 'Сделать админом'}
                    </button>
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-status-danger"
                      onClick={() => handleRemove(m)}
                    >
                      Удалить
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mx-auto max-w-[1200px]">
        <OrganizationGroupsSection isOrgAdmin={isAdmin} />
      </div>

      {rolesModalOpen && <OrganizationRolesModal isOrgAdmin={isAdmin} onClose={() => setRolesModalOpen(false)} />}
    </AppShell>
  );
}

function ReadOnlyField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-medium text-text-secondary">{label}</label>
      <p className="flex h-10 items-center rounded-[9px] border border-border-default bg-bg-surface-secondary px-3 text-[13px] text-text-primary">
        {value || '—'}
      </p>
    </div>
  );
}
